<template>
  <Teleport to="body">
    <div class="reward-coin-flight" aria-hidden="true">
      <img ref="coinEl" :src="resolvedCoin" alt="" class="reward-coin-flight__coin" />
      <div ref="labelEl" class="reward-coin-flight__label">+{{ formattedPoints }}</div>
      <div ref="impactEl" class="reward-coin-flight__impact" />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type ComponentPublicInstance, type PropType } from 'vue'
import gsap from 'gsap'
import defaultCoin from '@/../public/img/game/bolt.svg'

const props = defineProps({
  points: { type: Number, required: true },
  coinSrc: { type: String, default: '' },
  sourceEl: { type: Object as PropType<HTMLElement | ComponentPublicInstance | null>, default: null },
  targetEl: { type: Object as PropType<HTMLElement | ComponentPublicInstance | null>, default: null },
  sourceSelector: { type: String, default: '' },
  targetSelector: { type: String, default: '' },
  trigger: { type: [String, Number], default: 0 },
  autoplay: { type: Boolean, default: false },
})

const emit = defineEmits<{
  start: []
  arrive: [points: number]
  complete: []
  unavailable: [reason: string]
}>()

const coinEl = ref<HTMLImageElement | null>(null)
const labelEl = ref<HTMLElement | null>(null)
const impactEl = ref<HTMLElement | null>(null)
const resolvedCoin = computed(() => props.coinSrc || defaultCoin)
const formattedPoints = computed(() => props.points.toLocaleString('pt-BR'))
let timeline: gsap.core.Timeline | null = null

function toElement(value: HTMLElement | ComponentPublicInstance | null) {
  if (!value) return null
  const candidate = ((value as ComponentPublicInstance).$el ?? value) as Element | null
  return candidate && typeof candidate.getBoundingClientRect === 'function' ? candidate : null
}

function resolveElement(explicitEl: HTMLElement | ComponentPublicInstance | null, selector: string) {
  return toElement(explicitEl) ?? (selector ? document.querySelector<HTMLElement>(selector) : null)
}

async function play() {
  await nextTick()
  const source = resolveElement(props.sourceEl, props.sourceSelector)
  const target = resolveElement(props.targetEl, props.targetSelector)

  if (!source || !target || !coinEl.value || !labelEl.value || !impactEl.value) {
    emit('unavailable', 'RewardCoinFlight precisa de uma origem e um destino válidos.')
    return
  }

  timeline?.kill()
  gsap.killTweensOf([coinEl.value, labelEl.value, impactEl.value])

  const sourceRect = source.getBoundingClientRect()
  const targetRect = target.getBoundingClientRect()
  const coinSize = 44
  const sx = sourceRect.left + sourceRect.width * 0.78
  const sy = sourceRect.top + sourceRect.height * 0.28
  const ex = targetRect.left + targetRect.width * 0.5
  const ey = targetRect.top + targetRect.height * 0.5
  const dx = ex - sx
  const mx = sx + dx * 0.5
  const my = Math.min(sy, ey) - Math.max(72, Math.abs(dx) * 0.12)

  emit('start')
  gsap.set(coinEl.value, {
    x: sx - coinSize / 2,
    y: sy - coinSize / 2,
    scale: 0,
    opacity: 1,
    rotation: 0,
  })
  gsap.set(impactEl.value, {
    left: ex,
    top: ey,
    xPercent: -50,
    yPercent: -50,
    scale: 0.25,
    opacity: 0,
  })
  gsap.set(labelEl.value, {
    x: sx - 32,
    y: sy - 72,
    opacity: 0,
    scale: 0.88,
  })

  timeline = gsap.timeline({ onComplete: () => emit('complete') })

  timeline
    .to(labelEl.value, { opacity: 1, y: sy - 82, scale: 1, duration: 0.2, ease: 'power2.out' }, 0)
    .to(coinEl.value, { scale: 0.5, duration: 0.24, ease: 'back.out(2.2)' }, 0)
    .to(coinEl.value, { x: mx - coinSize / 2, y: my - coinSize / 2, scale: 2.5, duration: 0.42, ease: 'power2.out' }, 0.2)
    .to(coinEl.value, {
      x: ex - coinSize / 2,
      y: ey - coinSize / 2,
      scale: 0.46,
      duration: 0.36,
      ease: 'power2.in',
      onComplete: () => emit('arrive', props.points),
    }, 0.62)
    .to(coinEl.value, { opacity: 0, duration: 0.06 }, 0.96)
    .to(impactEl.value, { opacity: 0.7, scale: 1, duration: 0.13, ease: 'power2.out' }, 0.9)
    .to(impactEl.value, { opacity: 0, scale: 1.8, duration: 0.3, ease: 'power2.out' }, 1.03)
    .to(labelEl.value, { opacity: 0, y: sy - 92, duration: 0.22, ease: 'power2.in' }, 0.96)
}

defineExpose({ play })
watch(() => props.trigger, (value, oldValue) => { if (value !== oldValue) void play() })
onMounted(() => { if (props.autoplay) void play() })
onBeforeUnmount(() => timeline?.kill())
</script>

<style scoped>
.reward-coin-flight {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  width: 0;
  height: 0;
  pointer-events: none;
}
.reward-coin-flight__coin {
  position: fixed;
  left: 0;
  top: 0;
  display: block;
  width: 2.75rem;
  height: 2.75rem;
  max-width: none;
  opacity: 0;
  filter: drop-shadow(0 10px 18px rgba(109, 56, 158, .28));
  will-change: transform, opacity;
}
.reward-coin-flight__impact {
  position: fixed;
  width: 3.5rem;
  height: 3.5rem;
  border: 3px solid rgba(139, 92, 246, .55);
  border-radius: 9999px;
  opacity: 0;
  will-change: transform, opacity;
}
.reward-coin-flight__label {
  position: fixed;
  left: 0;
  top: 0;
  white-space: nowrap;
  border-radius: 9999px;
  background: #7c3aed;
  color: white;
  padding: .3rem .78rem;
  font-size: .875rem;
  line-height: 1.25rem;
  font-weight: 900;
  opacity: 0;
  box-shadow: 0 8px 24px rgba(76, 29, 149, .24);
  will-change: transform, opacity;
}
</style>
